import { useEffect } from 'react';

import { logInDev } from './log-in-dev.util';

type PersistFn<TData, TVars> = (entry: { data: TData; variables: TVars; savedAt: string }) => Promise<void>;

export type UsePersistQueryResultParams<TData, TVars> = {
  data: TData | undefined;
  variables: TVars;
  /** Async write to the persistent store. */
  persist: PersistFn<TData, TVars>;
  /** Skip persistence entirely when false (e.g. `persistToStorage && !skip`). */
  enabled: boolean;
  /** Don't persist while a request is still in flight. */
  loading: boolean;
};

/**
 * Persist a query's latest successful result via `persist(entry)`. Fires when `data`
 * transitions to populated AND `loading` is false AND `enabled` is true.
 *
 * `variables` is read from the closure — when `data` changes it reflects the variables
 * that produced that data, so it does not need to be a dep. This keeps the effect from
 * re-firing on every render of callers that build a fresh `variables` object inline.
 */
export const usePersistQueryResult = <TData, TVars>({
  data,
  variables,
  persist,
  enabled,
  loading,
}: UsePersistQueryResultParams<TData, TVars>): void => {
  useEffect(() => {
    if (!enabled || !data || loading) return;
    persist({ data, variables, savedAt: new Date().toISOString() }).catch(logInDev);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `variables` & `persist` read from closure
  }, [enabled, data, loading]);
};
